/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';

import { type GrammarList } from '../conditionalSearch/grammar.type';
import { batch, grammar } from './gammar';

const tipStyle = {
    style: { marginLeft: '1em', overflow: 'hidden', transform: 'translateY(-2px)' },
    value: 'i18n.get(home.search_term_sortByPerformance_info)',
};

const performanceGrammar = {
    'performanceData.getTopBatch': [{
        key: 'IOPS',
        value: 22,
        tip: tipStyle,
    }, {
        key: 'Bandwidth',
        value: 21,
        tip: tipStyle,
    }, {
        key: 'Latency',
        value: 370,
        tip: tipStyle,
    }],
};
const FileSystemPerformanceGrammar = {
    'performanceData.getTopBatch': [{
        key: 'ServiceTime',
        value: 523,
        tip: tipStyle,
    }, {
        key: 'FileBandwidth',
        value: 511,
        tip: tipStyle,
    }, {
        key: 'OPS',
        value: 182,
        tip: tipStyle,
    }],
};
export const LUNs = {
    'lun.getBatch': [
        {
            key: 'Name',
            value: 'NAME',
        }, {
            key: 'Capacity',
            value: 'CAPACITY',
        },
        // 微存储直通场景屏蔽已分配容量，仅支持总容量
        ...(SWITCH.IS_DIRECT_MODE ? [] : [
            {
                key: 'Allocated',
                value: 'ALLOCCAPACITY',
            },
        ]), {
            key: 'HealthStatus',
            value: 'HEALTHSTATUS',
            enumValue: { 1: 'Normal', 2: 'Faulty', 15: 'Writeprotected' },
        }, {
            key: 'RunningStatus',
            value: 'RUNNINGSTATUS',
            enumValue: { 27: 'Online', 28: 'Offline' },
        },
        // 微存储屏蔽功能类型
        ...(FeatureConfig.IsHideLunTableBusinessColumnDef ? [] : [
            {
                key: 'FunctionType',
                value: 'functionType',
                enumValue: { 1: 'LUN', 2: 'Snapshot', 3: 'Clone' },
            },
        ]), {
            key: 'Mapping',
            value: FeatureConfig.IsHideLunTableBusinessColumnDef ? 'EXPOSEDTOINITIATOR' : 'mapped',
        }, {
            key: 'LocalWWN',
            value: 'WWN',
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
export const LUNGroups = {
    'lunGroup.getBatch': [
        {
            key: 'Name',
            value: 'NAME',
        },
        {
            key: 'Mapping',
            value: 'ISADD2MAPPINGVIEW',
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
export const HOSTs = {
    'host.getBatch': [
        {
            key: 'Name',
            value: 'NAME',
        },
        {
            key: 'HealthStatus',
            value: 'HEALTHSTATUS',
            enumValue: { 1: 'Normal', 2: 'Faulty', 17: 'NoRedundantLink', 18: 'Offline' },
        },
        {
            key: 'OS',
            value: 'OPERATIONSYSTEM',
        },
        {
            key: 'IPAddress',
            value: 'IP',
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
export const HostGroups = {
    'hostGroup.getBatch': [
        {
            key: 'Name',
            value: 'NAME',
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
export const PortGroups = {
    'portGroup.getBatch': [
        {
            key: 'Name',
            value: 'NAME',
        },
        {
            key: 'Mapping',
            value: 'isAddToMappingView',
        },
    ],
};
export const LogicalPorts = {
    'lif.getBatch': [
        {
            key: 'Name',
            value: 'NAME',
        },
        {
            key: 'RunningStatus',
            value: 'RUNNINGSTATUS',
            // { 0: '未知', 1: '正常', 2: '运行', 10: '已连接', 11: '未连接', 53: '初始化中', 110: '待工作' }
            enumValue: {
                0: 'Unknown',
                1: 'Normal',
                2: 'Running',
                10: 'LinkUp',
                11: 'LinkDown',
                33: 'ToBeRecovered',
                53: 'Initializing',
                110: 'StandBy',
            },
        },
        {
            key: 'ActivationStatus',
            value: 'OPERATIONALSTATUS',
        },
        {
            key: 'DataProtocol',
            value: 'SUPPORTPROTOCOL',
            enumValue: SWITCH.SUPPORT_NAS ?
                { 0: 'None', 1: 'NFS', 2: 'CIFS', 3: 'NFS+CIFS', 4: 'iSCSI', 8: 'FC/FCoE', 64: 'NVMe_over_RoCE', 512: 'BGP' } :
                { 0: 'None', 64: 'NVMe_over_RoCE' },
        },
        {
            key: 'IPv4Address',
            value: 'IPV4ADDR',
        }, {
            key: 'IPv4SubnetMask',
            value: 'IPV4MASK',
        }, {
            key: 'IPv4Gateway',
            value: 'IPV4GATEWAY',
        }, {
            key: 'IPv6Address',
            value: 'IPV6ADDR',
        }, {
            key: 'IPv6SubnetMask',
            value: 'IPV6MASK',
        }, {
            key: 'IPv6Gateway',
            value: 'IPV6GATEWAY',
        }, {
            key: 'HomePort',
            value: 'HOMEPORTNAME',
        },
        ...(FeatureConfig.IsHideLogicPortCurrentPort ? [] : [
            {
                key: 'CurrentPort',
                value: 'CURRENTPORTNAME',
            },
        ]),
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
export const vStores = {
    'vstore.getBatch': [
        {
            key: 'Name',
            value: 'NAME',
        }, {
            key: 'RunningStatus',
            value: 'RUNNINGSTATUS',
            enumValue: { 1: 'normal', 53: 'initializing' },
        },
    ],
};
export const LUNSnapshots = {
    'snapshot.getSnapshot': [
        {
            key: 'Name',
            value: 'NAME',
        }, {
            key: 'HealthStatus',
            value: 'HEALTHSTATUS',
            enumValue: { 1: 'Normal', 2: 'Faulty', 15: 'Writeprotected' },
        }, {
            key: 'RunningStatus',
            value: 'RUNNINGSTATUS',
            enumValue: { 43: 'Activated', 44: 'Rollingback', 45: 'Unactivated' },
        }, {
            key: 'SourceLUN',
            value: 'SOURCELUNNAME',
        }, {
            key: 'ParentObject',
            value: 'PARENTNAME',
        }, {
            key: 'Activated',
            value: 'TIMESTAMP',
        },
        {
            key: 'LocalWWN',
            value: 'WWN',
        },
    ],
};
export const FileSystems = {
    'fileSystem.getBatch': [
        {
            key: 'Name',
            value: 'NAME',
        },
        {
            key: 'HealthStatus',
            value: 'HEALTHSTATUS',
            enumValue: { 1: 'Normal', 2: 'Faulty', 14: 'Invalid', 15: 'Writeprotected' },
        }, {
            key: 'RunningStatus',
            value: 'RUNNINGSTATUS',
            enumValue: { 27: 'Online', 28: 'Offline', 35: 'Invalid', 53: 'Initializing' },
        },
    ],
    ...FileSystemPerformanceGrammar,
};
export const keyMap = {
    LUNs,
    LUNGroups,
    Namespaces: LUNs,
    NamespaceGroups: LUNGroups,
    Hosts: HOSTs,
    HostGroups,
    PortGroups,
    LogicalPorts,
    vStores,
    LUNSnapshots,
    FileSystems,
};
const grammarList: GrammarList = batch(grammar, keyMap);
export default grammarList;
